import { useNavigate } from "react-router-dom";
import { useCycle } from "../../context/CycleContext";
import { getTeamAssessments } from "../../services/assessmentApi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, Loader, ErrorState, DataTable, PersonCell, StatusBadge, Button, Banner, RemindButton } from "../../components/common";
import RatingPill from "../../components/performance/RatingPill";
import { ASSESSMENT_STATUS, REMINDER_TASK_TYPES } from "../../utils/constants";

import { formatRatingScore } from "../../utils/ratingScale"; // [NEW] PM-13: score on the chosen scale

const api = { getTeamAssessments };

export default function TeamAssessmentsPage() {
  const { cycleId, cycle, isStageOpen } = useCycle();
  const navigate = useNavigate();
  const { data, error, reload } = useAsync(() => api.getTeamAssessments(cycleId), [cycleId], { skip: !cycleId });

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader />;

  const ready = data.filter((r) => r.selfStatus === ASSESSMENT_STATUS.SUBMITTED && r.managerStatus !== ASSESSMENT_STATUS.SUBMITTED).length;

  return (
    <div className="page">
      <PageHeader title="Team assessments" subtitle={`${ready} ready for your review in ${cycle?.name}.`} />
      {!isStageOpen("managerReview") && (
        <Banner tone="neutral" title="Manager review is not open">You can read assessments, but you can't submit until HR opens this stage.</Banner>
      )}
      <Card padded={false}>
        <DataTable
          rows={data}
          rowKey={(r) => r.employee.id}
          onRowClick={(r) => navigate(`/team/assessments/${r.employee.id}`)}
          columns={[
            { key: "name", label: "Employee", render: (r) => <PersonCell name={r.employee.name} meta={r.employee.designationName} /> },
            { key: "self", label: "Self assessment", render: (r) => <StatusBadge status={r.selfStatus} /> },
            { key: "mgr", label: "Your assessment", render: (r) => <StatusBadge status={r.managerStatus} /> },
            { key: "result", label: "Result", render: (r) => <StatusBadge status={r.resultStatus} /> },
            { key: "score", label: "Score", align: "right", render: (r) => formatRatingScore(r.performance.finalScore) },
            { key: "rating", label: "Rating", render: (r) => <RatingPill rating={r.selfStatus === ASSESSMENT_STATUS.SUBMITTED ? r.performance.rating : null} tone={r.performance.ratingTone} size="sm" /> },
            {
              key: "act", label: "", align: "right",
              render: (r) => (
                <span className="row-actions">
                  {r.selfStatus !== ASSESSMENT_STATUS.SUBMITTED && (
                    <RemindButton
                      recipients={[{ id: r.employee.id, name: r.employee.name }]}
                      taskType={REMINDER_TASK_TYPES.SELF_ASSESSMENT}
                      defaultMessage={`Please complete and submit your self assessment for ${cycle?.name || "this cycle"} so I can start your review.`}
                    />
                  )}
                  <Button
                    size="sm"
                    variant={r.selfStatus === ASSESSMENT_STATUS.SUBMITTED && r.managerStatus !== ASSESSMENT_STATUS.SUBMITTED ? "primary" : "ghost"}
                    onClick={(e) => { e.stopPropagation(); navigate(`/team/assessments/${r.employee.id}`); }}
                  >
                    {r.selfStatus === ASSESSMENT_STATUS.SUBMITTED && r.managerStatus !== ASSESSMENT_STATUS.SUBMITTED ? "Assess" : "View"}
                  </Button>
                </span>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
